/**
 * user-frontend/src/context/AuthContext.js
 *
 * FIXES:
 *  - Adicionado isLoading (era undefined → ProtectedRoute renderizava antes de hidratar)
 *  - Removida duplicação com Redux: Context é a fonte única de auth no user-frontend
 *  - user_id normalizado: sempre 'id' internamente
 *  - logout chama API antes de limpar estado local
 */

import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import { logoutUser, getMyProfile } from '../utils/api';
import { invalidarMinhasEmpresas } from '../hooks/minhasEmpresasCache';
import {
  sessaoExpiradaPorInactividade,
  marcarActividade,
  limparActividade,
  observarActividade,
} from '../utils/idleSession.js';

export const AuthContext = createContext(null);

// Hook conveniente — evita importar useContext em cada ficheiro
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // FIX: era inexistente

  // ─── Hidratação do localStorage ─────────────────────────────
  useEffect(() => {
    try {
      const token   = localStorage.getItem('authToken');
      const stored  = localStorage.getItem('user');
      // RN-062: sessão parada há mais de 8h não é retomada
      if (token && sessaoExpiradaPorInactividade()) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        limparActividade();
      } else if (token && stored && stored !== 'undefined') {
        const parsed = JSON.parse(stored);
        setUser(parsed);
        setIsAuthenticated(true);
        marcarActividade();
      }
    } catch {
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Regista a atividade do utilizador enquanto a sessão está aberta
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    return observarActividade();
  }, [isAuthenticated]);

  // ─── Sincronização entre separadores ────────────────────────
  // Se a sessão mudar noutro separador (ex.: login como trabalhador),
  // este separador atualiza-se — evita "UI de cliente + token de worker".
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'authToken' && e.key !== 'user') return;
      const token = localStorage.getItem('authToken');
      const stored = localStorage.getItem('user');
      if (token && stored && stored !== 'undefined') {
        try { setUser(JSON.parse(stored)); setIsAuthenticated(true); } catch { /* ignore */ }
      } else {
        invalidarMinhasEmpresas();
        setUser(null);
        setIsAuthenticated(false);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // ─── Login ───────────────────────────────────────────────────
  // Recebe o objecto retornado por loginAsync (já normalizado)
  const login = useCallback((userData) => {
    if (!userData?.token) {
      console.error('AuthContext.login: token em falta', userData);
      return false;
    }
    // Normalizar: garantir campo 'id' consistente
    const telefone = userData.user?.phone_number ?? userData.phone_number;
    const normalised = {
      id:          userData.user?.id   ?? userData.id,
      email:       userData.user?.email ?? userData.email,
      role:        userData.user?.role  ?? userData.role,
      full_name:   userData.user?.full_name ?? userData.full_name,
      // Só entra se a resposta o trouxer. Meter null apagava um número que
      // o ecrã de perfil já tinha ido buscar à conta.
      ...(telefone ? { phone_number: telefone } : {}),
      token:       userData.token,
    };
    localStorage.setItem('authToken', normalised.token);
    localStorage.setItem('user', JSON.stringify(normalised));
    marcarActividade();
    setUser(normalised);
    setIsAuthenticated(true);
    return true;
  }, []);

  /**
   * Actualiza o utilizador em memória e no localStorage depois de ele
   * editar o perfil. Sem isto, o nome no cabeçalho e na saudação ficavam
   * com o valor antigo até ao próximo início de sessão.
   */
  const refreshUser = useCallback((parcial) => {
    setUser((anterior) => {
      if (!anterior) return anterior;
      const actualizado = { ...anterior, ...(parcial || {}) };
      try { localStorage.setItem('user', JSON.stringify(actualizado)); } catch { /* quota */ }
      return actualizado;
    });
  }, []);

  // A sessão guardada no browser não traz o telefone (o login também não).
  // Sem esta leitura, o perfil mostrava «—» numa conta que o tinha desde o
  // formulário único de registo.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    let vivo = true;
    getMyProfile()
      .then((p) => {
        if (!vivo || !p) return;
        refreshUser({
          full_name: p.full_name,
          email: p.email,
          phone_number: p.phone_number || '',
          bio: p.bio || '',
        });
      })
      .catch(() => { /* o separador de perfil volta a tentar */ });
    return () => { vivo = false; };
  }, [isAuthenticated, refreshUser]);

  // ─── Logout ──────────────────────────────────────────────────
  const logout = useCallback(async () => {
    try {
      await logoutUser(); // notifica o backend (blacklist token)
    } catch {
      // ignora erro de rede — limpa localmente de qualquer forma
    }
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    limparActividade();
    // A lista de empresas é da sessão. Sem isto, a conta seguinte herdava
    // o cartão e o atalho de quem saiu.
    invalidarMinhasEmpresas();
    setUser(null);
    setIsAuthenticated(false);
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};
